import i18n from "i18next";

export const LANGUAGE_STORAGE_KEY = "blissnet_language" as const;

export const LANGUAGE_OPTIONS = [
  { code: "en", label: "English" },
  { code: "fr", label: "Français" },
  { code: "de", label: "Deutsch" },
  { code: "he", label: "עברית" },
  { code: "es", label: "Español" },
] as const;

export type LanguageCode = typeof LANGUAGE_OPTIONS[number]["code"];

export const VALID_CODES = LANGUAGE_OPTIONS.map((l) => l.code);

export function isLanguageCode(value: string): value is LanguageCode {
  return (VALID_CODES as readonly string[]).includes(value);
}

export function normalizeLanguageCode(value: unknown): LanguageCode | null {
  if (typeof value !== "string") return null;
  const base = value.toLowerCase().split("-")[0];
  return isLanguageCode(base) ? base : null;
}

export function getStoredLanguagePreference(): LanguageCode | null {
  if (typeof localStorage === "undefined") return null;
  return normalizeLanguageCode(localStorage.getItem(LANGUAGE_STORAGE_KEY));
}

export function detectSupportedBrowserLanguage(): LanguageCode {
  if (typeof navigator === "undefined") return "en";

  const candidates = [
    ...(Array.isArray(navigator.languages) ? navigator.languages : []),
    navigator.language,
  ];

  for (const candidate of candidates) {
    const normalized = normalizeLanguageCode(candidate);
    if (normalized) return normalized;
  }

  return "en";
}

export function resolveInitialLanguage(): LanguageCode {
  return getStoredLanguagePreference() ?? detectSupportedBrowserLanguage();
}

/**
 * Resolve text direction for a language tag using i18next's RTL catalog.
 * Accepts regional codes (e.g. he-IL, en-US). Not Chat-local and not Hebrew-only.
 */
export function resolveDocumentDirection(language: string): "rtl" | "ltr" {
  return i18n.dir(language) === "rtl" ? "rtl" : "ltr";
}

/**
 * Single authoritative owner of documentElement lang + dir.
 * Always sets both so switching RTL → LTR clears stale rtl.
 */
export function applyDocumentLanguage(
  lang: LanguageCode,
  languageForDir: string = lang
): void {
  if (typeof document === "undefined") return;
  document.documentElement.lang = lang;
  document.documentElement.dir = resolveDocumentDirection(languageForDir);
}
